import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, first, tap } from 'rxjs/operators';

import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { YesNoMessageboxComponent } from '@components/_base/_messageboxes/yes-no-messagebox/yes-no-messagebox.component';
import { SortingPagingData } from '@helpers/sorting-paging-data';
import { ExportService } from '@services/export.service';
import { displayError, displayErrorFromUnknown } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { prepareDateForDb, convertObjDateToString } from '@app/_utils/date-util';
import { modalMinWidth } from '@env/config';
import { PermissionsService } from '@app/login/_services/permissions.service';

import { ListBankStatement, MATCH_STATUSES } from '../_models/list-bank-statement.model';
import { MatchDialogResult } from '../_models/bank-statement-match.model';
import { BankStatementService } from '../_services/bank-statement.service';
import { MatchBankStatementDialogComponent } from '../match-bank-statement-dialog/match-bank-statement-dialog.component';

/**
 * The bank statements fetched from FIBank, and what the matcher made of each one.
 *
 * This is the operator's queue: everything the nightly matcher could not settle on its own -
 * an invoice number that parsed but resolved to nothing, a number several invoices answer to,
 * a transfer whose amount did not agree with what was owed - is left here with the reason
 * recorded, for a person to look at.
 */
@Component({
  selector: 'app-list-bank-statements',
  templateUrl: './list-bank-statements.component.html',
  styleUrls: ['./list-bank-statements.component.css']
})
export class ListBankStatementsComponent extends BaseSortableSearchComponent<ListBankStatement> implements OnInit, OnDestroy {

  matchStatuses = MATCH_STATUSES;

  /**
   * The payment types offered by the filter dropdown, fetched from the backend when the screen
   * opens. The bank writes these in Bulgarian already, so they are displayed as they come
   * rather than through a translation key.
   *
   * The same list the backend restricts the screen to, served rather than hardcoded here so
   * the dropdown cannot offer a type the query would then return nothing for.
   */
  paymentTypes: string[] = [];

  /** True while the export is building, so the button cannot be pressed twice. */
  loadExcel = false;

  searchForm = this.fb.group({
    matchStatus:         [null],
    reference:           [null],
    paymentDetails:      [null],
    invoiceNumber:       [null],
    amount:              [null],
    beneficiaryBankName: [null],
    paymentType:         [null],
    dateFrom:            [null],
    dateTo:              [null]
  });

  /**
   * True while a manual import or re-match is in flight. Both buttons watch it, not just
   * the one that was pressed: the two jobs run against the same rows and there is no sense
   * starting the matcher over statements the import is still bringing in.
   */
  working = false;

  searchSubscription: Subscription;
  paymentTypesSubscription: Subscription;

  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() =>
      this.bankStatementService.pageable(
        this.sortingPaging,
        this.searchForm.value,
        prepareDateForDb(this.searchForm.get('dateFrom')?.value),
        prepareDateForDb(this.searchForm.get('dateTo')?.value)).pipe(
          tap(([page, totalCount]) => {
            this.sortingPaging.fromRow = page.fromRow;
            this.sortingPaging.toRow = page.toRow;
            this.sortingPaging.totalElements = totalCount;
            this.content = page.content;
            this.dataLoaded = true;
          })
        )
    ),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      return EMPTY;
    })
  );

  constructor(
    private fb:                   FormBuilder,
    private dialog:               MatDialog,
    private translateService:     TranslateService,
    private bankStatementService: BankStatementService,
    private uiEvent:              UIEventCustom,
    private toastr:               ToastrService,
    private exportService:        ExportService,
    public  perms:                PermissionsService,
  ) {
    super();
  }

  ngOnInit() {
    this.searchSubscription = this.search$.subscribe();
    this.loadPaymentTypes();
    this.searchSubject.next();
  }

  /**
   * Fills the payment type dropdown from what is actually in the statements.
   *
   * A failure here is logged and otherwise swallowed: the list itself is perfectly usable
   * with one filter missing, and taking the whole screen down over a dropdown would be a
   * poor trade. The operator sees an empty select rather than an error page.
   */
  private loadPaymentTypes() {
    this.paymentTypesSubscription = this.bankStatementService.findPaymentTypes().subscribe({
      next: paymentTypes => this.paymentTypes = paymentTypes ?? [],
      error: err => displayError(err)
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.paymentTypesSubscription?.unsubscribe();
  }

  /**
   * Exports what the current filters select - not just the page on screen.
   *
   * The rows are re-fetched with the page size widened to the known total, which is how every
   * other export on this system works. Bounded in practice, since the screen only ever returns
   * the three transfer types rather than the whole statement table.
   */
  async exportExcel() {
    this.loadExcel = true;
    try {
      this.exportService.exportAsExcelFile(
        await this.prepareDataForExcel(),
        this.getHeadings(),
        this.getFilterBody(),
        this.getFilterHeading(),
        this.translateService.instant('exportData.filesNames.bankStatementsResult'));
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadExcel = false;
    }
  }

  /** The rows the current filters select, in the order and shape the table shows them. */
  private async prepareDataForExcel() {
    const content: any[] = [];

    const allRows = new SortingPagingData(this.sortingPaging.totalElements);
    allRows.pageSize = this.sortingPaging.totalElements;
    allRows.sortBy = this.sortingPaging.sortBy;
    allRows.sortAsc = this.sortingPaging.sortAsc;

    const result = await this.bankStatementService.findPageable(
      allRows,
      this.searchForm.value,
      prepareDateForDb(this.searchForm.get('dateFrom')?.value),
      prepareDateForDb(this.searchForm.get('dateTo')?.value)).pipe(first()).toPromise();

    result.content.forEach(statement => {
      content.push([
        statement.reference,
        statement.transactionDate,
        // Amount and currency together, as the table shows them: a bare number in a column
        // of transfers that are not all in one currency invites a wrong total.
        `${statement.amount} ${statement.currency ?? ''}`.trim(),
        statement.paymentType,
        statement.paymentDetails,
        statement.beneficiaryBankName,
        // Translated, not the raw enum - the reader has only ever seen the Bulgarian label.
        this.translateService.instant(this.matchStatusKey(statement.matchStatus)),
        statement.matchNote,
        statement.matchedInvoiceNumbers
      ]);
    });

    return content;
  }

  private getHeadings(): string[][] {
    return [[
      this.translateService.instant('listBankStatement.reference'),
      this.translateService.instant('listBankStatement.transactionDate'),
      this.translateService.instant('listBankStatement.amount'),
      this.translateService.instant('listBankStatement.paymentType'),
      this.translateService.instant('listBankStatement.paymentDetails'),
      this.translateService.instant('listBankStatement.beneficiaryBankName'),
      this.translateService.instant('listBankStatement.matchStatus'),
      this.translateService.instant('listBankStatement.matchNote'),
      this.translateService.instant('listBankStatement.matchedInvoiceNumbers')
    ]];
  }

  /** The filters as applied, so a saved file still says what it was a view of. */
  private getFilterBody(): any[] {
    const matchStatus = this.searchForm.get('matchStatus')?.value;

    return [[
      matchStatus ? this.translateService.instant(this.matchStatusKey(matchStatus)) : '',
      this.searchForm.get('reference')?.value,
      this.searchForm.get('paymentDetails')?.value,
      this.searchForm.get('invoiceNumber')?.value,
      this.searchForm.get('amount')?.value,
      this.searchForm.get('beneficiaryBankName')?.value,
      this.searchForm.get('paymentType')?.value,
      convertObjDateToString(this.searchForm.get('dateFrom')?.value),
      convertObjDateToString(this.searchForm.get('dateTo')?.value)
    ]];
  }

  private getFilterHeading(): any[][] {
    const labels: any[] = [
      this.translateService.instant('listBankStatement.matchStatus'),
      this.translateService.instant('listBankStatement.reference'),
      this.translateService.instant('listBankStatement.paymentDetails'),
      this.translateService.instant('listBankStatement.invoiceNumber'),
      this.translateService.instant('listBankStatement.amount'),
      this.translateService.instant('listBankStatement.beneficiaryBankName'),
      this.translateService.instant('listBankStatement.paymentType'),
      this.translateService.instant('listBankStatement.dateFrom'),
      this.translateService.instant('listBankStatement.dateTo')
    ];

    return [
      [{ content: this.translateService.instant('listBankStatement.title'),
         colSpan: labels.length, styles: { halign: 'center' } }],
      labels
    ];
  }

  /** Translation key for a match status, so the table shows Bulgarian rather than the enum. */
  matchStatusKey(matchStatus: string): string {
    return matchStatus ? 'listBankStatement.status.' + matchStatus : '';
  }

  /** Colours the status so an operator can pick the rows needing work out of the page. */
  matchStatusClass(matchStatus: string): string {
    switch (matchStatus) {
      case 'MATCHED':           return 'badge badge-success';
      case 'PARTIALLY_MATCHED': return 'badge badge-info';
      case 'AMBIGUOUS':         return 'badge badge-warning';
      case 'UNMATCHED':         return 'badge badge-danger';
      case 'IGNORED':           return 'badge badge-secondary';
      // Darker than UNMATCHED on purpose: this row is not in a queue waiting to be
      // reconsidered, it is one the matcher has given up on and only a person can move.
      case 'FAILED':            return 'badge badge-dark';
      default:                  return 'badge badge-light';
    }
  }

  /**
   * Fetches the last 30 days from the bank by hand, for when the nightly import did not run
   * or failed. Safe to repeat: the import is idempotent on the bank's own reference.
   */
  importRecent() {
    this.confirmThenRun('listBankStatement.confirmImport', () =>
      this.bankStatementService.importRecent().subscribe({
        next: run => {
          this.working = false;
          if (!run.started) {
            this.uiEvent.displayUIError('listBankStatement.alreadyRunning');
            return;
          }
          if (run.errorText) {
            // The bank did not answer. Distinct from "no new statements", and the only
            // one of the two that needs somebody to do something about it.
            this.uiEvent.displayUIError('listBankStatement.importFailed');
            return;
          }
          // The toast is built here rather than through displayUISuccess, which translates
          // the key it is given but takes no interpolation params - and these counts are
          // the whole point of the message.
          this.toastr.success(this.translateService.instant('listBankStatement.imported', {
            fetched: run.fetched, stored: run.stored, skipped: run.skipped, failed: run.failed
          }));
          this.searchSubject.next();
        },
        error: err => this.reportError(err)
      }));
  }

  /** Re-runs the matcher over the recent statements that are still unresolved. */
  rematchRecent() {
    this.confirmThenRun('listBankStatement.confirmRematch', () =>
      this.bankStatementService.rematchRecent().subscribe({
        next: run => {
          this.working = false;
          if (!run.started) {
            this.uiEvent.displayUIError('listBankStatement.alreadyRunning');
            return;
          }
          this.toastr.success(this.translateService.instant('listBankStatement.rematched', {
            considered: run.considered, matched: run.matched, failed: run.failed, writtenOff: run.writtenOff
          }));
          // A capped run looks exactly like a complete one otherwise, and the statements it
          // never reached keep the status they had - so it has to say so.
          if (run.reachedLimit) {
            this.toastr.warning(this.translateService.instant('listBankStatement.rematchReachedLimit'));
          }
          this.searchSubject.next();
        },
        error: err => this.reportError(err)
      }));
  }

  /**
   * Confirms, then runs - both jobs are worth a second thought before starting. One calls
   * the bank and the other moves invoices to paid in bulk, and both take long enough that
   * a second click while the first is still going is a natural mistake to make.
   */
  private confirmThenRun(messageKey: string, run: () => void) {
    if (this.working || this.dialog.openDialogs.length > 0) {
      return;
    }

    const ref = this.dialog.open(YesNoMessageboxComponent, {
      ...modalMinWidth,
      // isWarning false gives a real Yes/No pair; true would render a lone OK button.
      data: { isWarning: false, message: messageKey }
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.working = true;
      run();
    });
  }

  private reportError(err: any) {
    this.working = false;
    displayError(err);
    this.uiEvent.displayUIError();
  }

  /**
   * Opens the manual matching dialog for one statement, and refreshes the list afterwards -
   * but only if something was actually linked or unlinked, since the dialog is just as often
   * opened to look and closed again.
   */
  openMatchDialog(statement: ListBankStatement) {
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    const ref = this.dialog.open(MatchBankStatementDialogComponent, {
      ...modalMinWidth,
      data: {
        statementId: statement.id,
        reference: statement.reference,
        amount: statement.amount,
        currency: statement.currency
      }
    });

    ref.afterClosed().subscribe((result: MatchDialogResult | undefined) => {
      if (result?.changed) {
        this.searchSubject.next();
      }
    });
  }
}
